<template>
  <aside class="iq-side-panel" data-testid="iq-side-panel" aria-label="IQ chat">
    <header class="iq-side-panel__header">
      <StandardButton
        type="magenta"
        variant="outline"
        size="xs"
        label="New Chat"
        data-testid="iq-side-panel-new-chat"
        @click="emit('new-chat')"
      />
      <div class="iq-side-panel__header-actions">
        <button
          type="button"
          class="iq-side-panel__icon-btn"
          aria-label="Maximize"
          data-testid="iq-side-panel-maximize"
          @click="emit('maximize')"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          class="iq-side-panel__icon-btn"
          aria-label="Close"
          data-testid="iq-side-panel-close"
          @click="emit('close')"
        >
          <Icon name="interface_close" color="dark" size="small" />
        </button>
      </div>
    </header>

    <div class="iq-side-panel__body">
      <ExScrollBar ref="threadScrollEl" scroll-container-test-id="iq-side-panel-body">
        <div class="iq-side-panel__thread">
          <template v-if="showThread">
            <div v-for="item in visibleThreadItems" :key="item.key" class="iq-side-panel__row">
              <IQSidePanelUserMessage
                v-if="item.type === 'user'"
                :message="item.text"
                :attachment-refs="item.attachmentRefs"
                :mentions="item.mentions"
              />
              <IQSidePanelAIMessage
                v-else
                :message="item.text"
                :sources="item.sources"
                :is-error="item.isError"
                :can-retry="item.canRetry"
                @retry="emit('retry')"
              />
            </div>
            <div
              v-if="waiting"
              class="iq-side-panel__row"
              data-testid="iq-side-panel-thinking"
              role="status"
              aria-live="polite"
              aria-label="IQ is thinking"
            >
              <IQThinkingBanner
                state="thinking"
                title="Thinking..."
                caption="Analyzing role..."
                reasoning=""
                :use-store-reasoning="false"
                :hide-elapsed-time="true"
              />
            </div>
          </template>
          <p
            v-else
            class="iq-side-panel__greeting"
            data-testid="iq-side-panel-greeting"
            v-text="greetingText"
          />
        </div>
      </ExScrollBar>
    </div>

    <div class="iq-side-panel__footer">
      <AskAQuestionPrompt
        :placeholder="placeholder"
        :disabled="isInputDisabled"
        :generating="waiting"
        :attachments="attachments"
        :mention-source="mentionSource"
        @submit="handlePromptSubmit"
        @stop="emit('stop')"
        @attach="emit('attach')"
        @remove-attachment="emit('remove-attachment', $event)"
        @paste-attachment="emit('paste-attachment', $event)"
        @add-attachment="emit('add-attachment', $event)"
      />
    </div>
  </aside>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onMounted, ref, watch } from "vue";
import AskAQuestionPrompt from "@components/AskAQuestionPrompt/AskAQuestionPrompt.vue";
import { normalizeMentions } from "@components/AskAQuestionPrompt/mentionTokens";
import ExScrollBar from "@components/ExScrollBar/ExScrollBar.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import { LD_FLAG_IDEA_MDP_582 } from "@components/constants/launchDarklyFlagKeys";
import Icon from "@components/icons/Icon.vue";
import IQThinkingBanner from "@components/IQThinkingBanner/IQThinkingBanner.vue";
import IQSidePanelAIMessage from "./IQSidePanelAIMessage.vue";
import IQSidePanelUserMessage from "./IQSidePanelUserMessage.vue";
import {
  extractIQSidePanelMessageText,
  extractIQSidePanelSources,
  iqSidePanelMessageKey,
} from "./extractIQSidePanelMessageText";

const props = defineProps({
  messages: {
    type: Array,
    default: () => [],
  },
  userName: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "Ask anything. You can mention employees with @",
  },
  inputDisabled: {
    type: Boolean,
    default: false,
  },
  /**
   * True while IQ is generating a reply. Shows IQThinkingBanner in place of
   * the AI row and disables the chat input. Parent sets this on send and
   * clears it when the response arrives or the request errors.
   */
  waiting: {
    type: Boolean,
    default: false,
  },
  attachments: {
    type: Array,
    default: () => [],
  },
  /** Resolves `@` mention searches; omit to turn mentions off. */
  mentionSource: {
    type: Function,
    default: null,
  },
  /**
   * Storybook / tests override. When omitted, Comment Posted is gated by
   * LaunchDarkly idea-MDP-582 (fail-closed until the flag resolves true).
   */
  enableCommentPosted: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "new-chat",
  "maximize",
  "close",
  "submit",
  "stop",
  "attach",
  "remove-attachment",
  "paste-attachment",
  "add-attachment",
  "retry",
]);

const handlePromptSubmit = (text, mentions = []) => {
  emit("submit", text, mentions);
};

const app = getCurrentInstance();
const { proxy } = app || {};
const commentPostedFlag = ref(false);

onMounted(async () => {
  try {
    if (proxy && typeof proxy.$getFlag === "function") {
      commentPostedFlag.value = await proxy.$getFlag(LD_FLAG_IDEA_MDP_582, false);
    }
  } catch {
    commentPostedFlag.value = false;
  }
});

const isCommentPostedEnabled = computed(() => props.enableCommentPosted || commentPostedFlag.value);

const threadItems = computed(() =>
  (props.messages || [])
    .map((message, index, list) => {
      if (!message || typeof message !== "object") {
        return null;
      }
      const type = message.type === "ai" || message.type === "user" ? message.type : null;
      if (!type) {
        return null;
      }
      const attachmentRefs = Array.isArray(message.attachmentRefs) ? message.attachmentRefs : [];
      const mentions = type === "user" ? normalizeMentions(message.mentions) : [];
      const isError = type === "ai" && message.isError === true;
      return {
        key: iqSidePanelMessageKey(message),
        type,
        text: extractIQSidePanelMessageText(message.content),
        sources: extractIQSidePanelSources(message.content),
        attachmentRefs,
        mentions,
        isError,
        canRetry: isError && index === list.length - 1,
      };
    })
    .filter(Boolean)
);

const hasThread = computed(() => threadItems.value.length > 0);

const showThread = computed(
  () => hasThread.value && (props.waiting || isCommentPostedEnabled.value)
);

const visibleThreadItems = computed(() => {
  const items = threadItems.value;
  if (!props.waiting || !items.length) {
    return items;
  }
  const last = items[items.length - 1];
  return last.type === "ai" ? items.slice(0, -1) : items;
});

const isInputDisabled = computed(() => props.inputDisabled || props.waiting);

const threadScrollEl = ref(null);

const scrollThreadToLatest = () => {
  threadScrollEl.value?.scrollToBottom?.();
  threadScrollEl.value?.syncScrollbar?.();
};

watch(
  () => [
    props.waiting,
    showThread.value,
    visibleThreadItems.value.map((item) => item.key).join("|"),
  ],
  async () => {
    await nextTick();
    scrollThreadToLatest();
    await nextTick();
    scrollThreadToLatest();
  },
  { immediate: true }
);

const greetingText = computed(() => {
  const name = (props.userName || "").trim();
  return name ? `Hi ${name}, how can I help?` : "Hi, how can I help?";
});
</script>

<style scoped>
.iq-side-panel {
  display: flex;
  flex-direction: column;
  width: 400px;
  height: 100%;
  min-height: 0;
  background: #ffffff;
  border-left: 1px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
  box-sizing: border-box;
}

.iq-side-panel__header {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 56px;
  padding: 0 20px;
  border-bottom: 1px solid #e5e7eb;
  box-sizing: border-box;
}

.iq-side-panel__header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.iq-side-panel__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
}

.iq-side-panel__icon-btn:hover {
  background: #f3f4f6;
}

.iq-side-panel__icon-btn:focus-visible {
  outline: 2px solid #6366f1;
  outline-offset: 2px;
}

.iq-side-panel__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.iq-side-panel__thread {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  padding: 24px;
  box-sizing: border-box;
}

.iq-side-panel__row {
  width: 100%;
}

.iq-side-panel__greeting {
  margin: 0;
  width: 100%;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 26px;
  line-height: 32px;
  color: #111827;
}

.iq-side-panel__footer {
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 22px 20px;
  box-sizing: border-box;
}
</style>
